import styles from '../../index.module.scss';
import { formatCurrency } from 'components/EmployeeCost/SmartImport/steps/step4Confirm';
import type { WizardState } from 'components/EmployeeCost/SmartImport/wizardReducer';
import type { SmartImportChat } from '../useSmartImportChat';

// Ordered, de-duplicated target field keys — mirrors the modal wizard's Step 4
// (multiple source columns can map to one field; column order follows Step 2).
function fieldKeysFor(state: WizardState): string[] {
	const seen = new Set<string>();
	const ordered: string[] = [];
	for (const m of state.columnMappings) {
		if (m.category === 'employee_name' || m.category === 'skip' || m.fieldRef.type === 'none') continue;
		const key = m.fieldRef.type === 'existing' ? m.fieldRef.fieldId : m.fieldRef.tempId;
		if (!seen.has(key)) {
			seen.add(key);
			ordered.push(key);
		}
	}
	for (const r of state.aggregatedRows) {
		for (const k of Object.keys(r.values)) {
			if (!seen.has(k)) {
				seen.add(k);
				ordered.push(k);
			}
		}
	}
	return ordered;
}

export default function PreviewCard({ chat }: { chat: SmartImportChat }) {
	const { session, fieldNames } = chat;
	const employeeByRowId = new Map(session.employeeRows.map((r) => [r.rowId, r]));
	const importableRows = session.aggregatedRows.filter((row) => employeeByRowId.get(row.rowId)?.skipped !== true);
	const fieldKeys = fieldKeysFor(session);

	const headerLabel = (key: string): string =>
		chat.isPendingFieldKey(key) ? session.pendingFields.find((pf) => pf.tempId === key)?.label ?? key : fieldNames[key] ?? key;

	const totals: Record<string, number> = {};
	for (const key of fieldKeys) {
		totals[key] = importableRows.reduce((acc, row) => acc + (row.values[key] ?? 0), 0);
	}

	const anyNegated = session.columnMappings.some(
		(m) => m.negate && m.category !== 'skip' && m.category !== 'employee_name' && m.fieldRef.type !== 'none'
	);

	return (
		<div className={styles['action-card']}>
			<div className={styles['action-card__summary']}>
				Adding {importableRows.length} row{importableRows.length === 1 ? '' : 's'} into {fieldKeys.length} cost
				field{fieldKeys.length === 1 ? '' : 's'} for {chat.payPeriodLabel || 'the selected pay period'}.
				{anyNegated && ' (Some fields negated.)'}
			</div>
			<div className={styles['import-table-wrap']}>
				<table className={styles['import-table']}>
					<thead>
						<tr>
							<th>Employee</th>
							{fieldKeys.map((key) => (
								<th key={key}>{headerLabel(key)}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{importableRows.map((row) => {
							const twin = employeeByRowId.get(row.rowId);
							const isNew = twin?.matchStatus === 'new';
							const displayName = isNew ? twin?.addAsNewName ?? row.csvName : twin?.matchedEmployeeName ?? row.csvName;
							return (
								<tr key={row.rowId}>
									<td>
										{displayName} {isNew && <span className={styles['import-table__badge']}>new</span>}
									</td>
									{fieldKeys.map((key) => (
										<td key={key}>{formatCurrency(row.values[key] ?? 0)}</td>
									))}
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr>
							<td>Total</td>
							{fieldKeys.map((key) => (
								<td key={key}>{formatCurrency(totals[key] ?? 0)}</td>
							))}
						</tr>
					</tfoot>
				</table>
			</div>

			<div className={styles['smart-import-card__buttons-wrap']}>
				<button type="button" className={styles['action-card__cancel']} onClick={() => chat.fixStep(2)}>
					← Edit employees
				</button>
				<button type="button" className={styles['action-card__cancel']} onClick={chat.cancelImport}>
					Cancel
				</button>
				<button
					type="button"
					className={styles['action-card__confirm']}
					disabled={chat.submitting || !chat.payPeriodId}
					onClick={() => void chat.confirmImport()}
				>
					{chat.submitting ? 'Importing…' : 'Confirm Import'}
				</button>
			</div>
		</div>
	);
}
